import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { HttpError } from '../../utils/HttpError';
import { buildEfJsonModelUrl, buildEfHtmlModelUrl, AssetLocale } from './asset-service';
import { useLocale } from './useLocale';

type ResponseModel = 'json' | 'html';

type UseEfOptions<T> = {
  path: string;
  responseModel?: ResponseModel;
  override?: AssetLocale;
  select?: (raw: unknown) => T;
};

export type EfHtmlResult = { html: string };

export function useExperienceFragment<T = unknown>({
  path,
  responseModel = 'json',
  override,
  select,
}: UseEfOptions<T>) {
  const { current, fallback } = useLocale();

  const urls = useMemo(() => {
    const cur = override ?? current;
    const build = responseModel === 'json' ? buildEfJsonModelUrl : buildEfHtmlModelUrl;
    return {
      primary: build(path, cur),
      fallback: build(path, fallback),
    };
  }, [path, responseModel, override, current, fallback]);

  return useQuery({
    queryKey: ['ef', responseModel, path, override ?? current],
    queryFn: async ({ signal }) => {
      try {
        const r1 = await fetch(urls.primary, { signal });
        if (!r1.ok) throw new HttpError(r1.status, `HTTP error! status: ${r1.status}`);
        const data = responseModel === 'html' ? await r1.text() : await r1.json();
        return responseModel === 'html' ? { html: data as string } : (data as unknown);
      } catch (err) {
        if (urls.primary === urls.fallback) throw err;
        const r2 = await fetch(urls.fallback, { signal });
        if (!r2.ok) throw new HttpError(r2.status, `HTTP error! status: ${r2.status}`);
        const data = responseModel === 'html' ? await r2.text() : await r2.json();
        return responseModel === 'html' ? { html: data as string } : (data as unknown);
      }
    },
    select: (data) => {
      if (responseModel === 'html') return data as unknown as T;
      return select ? select(data) : (data as T);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
